import React, { useCallback, useEffect, useMemo, useState } from "react";

import HawkeyePage, { type HawkeyePageProps } from "./HawkeyePage";
import type { XFactorKpiMatrixGranularity } from "../Hooks/useXFactorKpiMatrix";
import { useAppDispatch } from "../Store/Hooks";
import { fetchMetricsTimePeriods } from "../Store/Reducers/MetricsSlice";

type DataSource = "hawkeye" | "digitalocean";

type EnterpriseKpisHawkeyeProps = HawkeyePageProps & {
  dataSource?: DataSource;
  ingestDate?: string | null;
  digestFromDate?: string | null;
  digestToDate?: string | null;
};

type PeriodOption = {
  value: string;
  label: string;
  /** Period-end measurement date, bounded by real timeseries max. */
  measurementDate: string;
  fromDate: string;
  toDate: string;
};

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toDate = (value: string | null | undefined): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Build period options from real timeseries measurement_date bounds
 * (same idea as HawkeyePage) — never invent future month-ends with no data
 * (e.g. yearly 2026 → 2026-12-31 when latest data is January).
 */
const buildPeriodOptions = (
  granularity: XFactorKpiMatrixGranularity,
  minMeasurementDate: string | null,
  maxMeasurementDate: string | null
): PeriodOption[] => {
  const options: PeriodOption[] = [];
  const fallbackToday = new Date();
  const maxDate =
    toDate(maxMeasurementDate) ??
    new Date(
      fallbackToday.getFullYear(),
      fallbackToday.getMonth(),
      fallbackToday.getDate()
    );
  const minDate = toDate(minMeasurementDate) ?? new Date(maxDate);
  if (minDate > maxDate) return [];

  if (granularity === "monthly") {
    let cursor = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    while (true) {
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      if (end < minDate) break;
      const boundedStart = start < minDate ? minDate : start;
      const boundedEnd = end > maxDate ? maxDate : end;
      options.push({
        value: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
        label: start.toLocaleString("en-US", {
          month: "long",
          year: "numeric",
        }),
        measurementDate: formatDate(boundedEnd),
        fromDate: formatDate(boundedStart),
        toDate: formatDate(boundedEnd),
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
    }
  } else if (granularity === "quarterly") {
    const currentQuarterStartMonth = Math.floor(maxDate.getMonth() / 3) * 3;
    let quarterStart = new Date(maxDate.getFullYear(), currentQuarterStartMonth, 1);
    while (true) {
      const quarterEnd = new Date(
        quarterStart.getFullYear(),
        quarterStart.getMonth() + 3,
        0
      );
      if (quarterEnd < minDate) break;
      const boundedStart = quarterStart < minDate ? minDate : quarterStart;
      const boundedEnd = quarterEnd > maxDate ? maxDate : quarterEnd;
      const quarterNumber = Math.floor(quarterStart.getMonth() / 3) + 1;
      options.push({
        value: `${quarterStart.getFullYear()}-Q${quarterNumber}`,
        label: `Q${quarterNumber} ${quarterStart.getFullYear()}`,
        measurementDate: formatDate(boundedEnd),
        fromDate: formatDate(boundedStart),
        toDate: formatDate(boundedEnd),
      });
      quarterStart = new Date(
        quarterStart.getFullYear(),
        quarterStart.getMonth() - 3,
        1
      );
    }
  } else if (granularity === "yearly") {
    let year = maxDate.getFullYear();
    const minYear = minDate.getFullYear();
    while (year >= minYear) {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);
      const boundedStart = yearStart < minDate ? minDate : yearStart;
      const boundedEnd = yearEnd > maxDate ? maxDate : yearEnd;
      options.push({
        value: `${year}`,
        label: `${year}`,
        measurementDate: formatDate(boundedEnd),
        fromDate: formatDate(boundedStart),
        toDate: formatDate(boundedEnd),
      });
      year -= 1;
    }
  } else {
    let weekEnd = new Date(maxDate);
    while (weekEnd >= minDate) {
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekEnd.getDate() - 6);
      const boundedStart = weekStart < minDate ? minDate : weekStart;
      const boundedEnd = weekEnd > maxDate ? maxDate : weekEnd;
      options.push({
        value: formatDate(boundedEnd),
        label: `${boundedStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} – ${boundedEnd.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`,
        measurementDate: formatDate(boundedEnd),
        fromDate: formatDate(boundedStart),
        toDate: formatDate(boundedEnd),
      });
      weekEnd = new Date(boundedStart);
      weekEnd.setDate(weekEnd.getDate() - 1);
    }
  }

  return options;
};

/**
 * Additive wrapper around the Enterprise KPIs / Account Health tab's data
 * Data source is locked to "digitalocean" in the UI (Hawkeye toggle hidden).
 * HawkeyePage still accepts a dataSource prop for the legacy path if re-enabled.
 *
 * Swap-in replacement for `<HawkeyePage {...props} />` at its three call
 * sites (AnyTab, MonitoredTab, PortfolioTab) — same props, forwarded as-is.
 * Keeps the full-height flex layout from the Data Hub RO UI work.
 */
const EnterpriseKpisPanel: React.FC<HawkeyePageProps> = props => {
  // UI locked to digitalocean — Hawkeye toggle hidden (legacy path retained in types).
  const dataSource: DataSource = "digitalocean";
  const dispatch = useAppDispatch();
  /** Exact-date override removed — period picker only. */
  const [granularity, setGranularity] =
    useState<XFactorKpiMatrixGranularity>("monthly");
  const [periodBounds, setPeriodBounds] = useState<{
    minMeasurementDate: string | null;
    maxMeasurementDate: string | null;
    maxCreatedDate: string | null;
  }>({
    minMeasurementDate: null,
    maxMeasurementDate: null,
    maxCreatedDate: null,
  });

  const loadTimePeriods = useCallback(async () => {
    try {
      const content = await dispatch(fetchMetricsTimePeriods()).unwrap();
      const minMeasurementDate =
        content?.min_measurement_date ?? content?.min_created_date ?? null;
      const maxMeasurementDate =
        content?.max_measurement_date ?? content?.max_created_date ?? null;
      const maxCreatedDate =
        content?.max_created_date ?? content?.max_measurement_date ?? null;
      setPeriodBounds({
        minMeasurementDate:
          typeof minMeasurementDate === "string" ? minMeasurementDate : null,
        maxMeasurementDate:
          typeof maxMeasurementDate === "string" ? maxMeasurementDate : null,
        maxCreatedDate:
          typeof maxCreatedDate === "string" ? maxCreatedDate : null,
      });
    } catch (error) {
      console.warn("Failed to load metrics time periods:", error);
      setPeriodBounds({
        minMeasurementDate: null,
        maxMeasurementDate: null,
        maxCreatedDate: null,
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (dataSource === "digitalocean") loadTimePeriods();
  }, [dataSource, loadTimePeriods]);

  const periodOptions = useMemo(
    () =>
      buildPeriodOptions(
        granularity,
        periodBounds.minMeasurementDate,
        periodBounds.maxMeasurementDate
      ),
    [
      granularity,
      periodBounds.minMeasurementDate,
      periodBounds.maxMeasurementDate,
    ]
  );
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  useEffect(() => {
    if (!periodOptions.length) return;
    setSelectedPeriod(prev =>
      periodOptions.some(option => option.value === prev)
        ? prev
        : periodOptions[0].value
    );
  }, [periodOptions]);

  const activePeriodOption = useMemo(() => {
    if (!periodOptions.length) return null;
    return (
      periodOptions.find(p => p.value === selectedPeriod) ?? periodOptions[0]
    );
  }, [periodOptions, selectedPeriod]);

  const effectiveMeasurementDate =
    activePeriodOption?.measurementDate ??
    periodBounds.maxMeasurementDate ??
    null;
  const effectiveDigestFromDate =
    activePeriodOption?.fromDate ?? effectiveMeasurementDate;
  const effectiveDigestToDate =
    activePeriodOption?.toDate ?? effectiveMeasurementDate;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <HawkeyePage
          {...({
            ...props,
            dataSource,
            measurementDate:
              dataSource === "digitalocean" ? effectiveMeasurementDate : null,
            ingestDate:
              dataSource === "digitalocean"
                ? periodBounds.maxCreatedDate
                : null,
            digestFromDate:
              dataSource === "digitalocean" ? effectiveDigestFromDate : null,
            digestToDate:
              dataSource === "digitalocean" ? effectiveDigestToDate : null,
            toolbarEnd:
              dataSource === "digitalocean" ? (
                <>
                  <span className="shrink-0 text-xs text-gray-500">
                    Granularity:
                  </span>
                  <select
                    value={granularity}
                    onChange={e => {
                      setGranularity(
                        e.target.value as XFactorKpiMatrixGranularity
                      );
                      setSelectedPeriod("");
                    }}
                    className="h-7 min-w-[110px] shrink-0 rounded border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700"
                    aria-label="Select granularity"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <span className="ml-1 shrink-0 text-xs text-gray-500">
                    Period:
                  </span>
                  <select
                    value={activePeriodOption?.value ?? ""}
                    onChange={e => setSelectedPeriod(e.target.value)}
                    disabled={!periodOptions.length}
                    className="h-7 shrink-0 rounded border border-gray-200 bg-white px-2 text-xs disabled:opacity-50"
                    aria-label="Select period"
                  >
                    {periodOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </>
              ) : null,
          } as EnterpriseKpisHawkeyeProps)}
        />
      </div>
    </div>
  );
};

export default EnterpriseKpisPanel;
